import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { signInWithPopup, getIdToken } from 'firebase/auth';
import { auth, googleProvider } from '../utils/firebase';
import { AuthContext } from '../contexts/AuthContext';
import { googleSignIn as apiGoogleSignIn, getSubscriptionStatus } from '../utils/api';
import { extractErrorMessage, shouldSuggestSignUp, shouldSuggestSignIn } from '../utils/errorMessages';
import SEO from './SEO';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { authenticate, loading, user } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [suggestSignUp, setSuggestSignUp] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [suggestSignIn, setSuggestSignIn] = useState(false);

  // Check if user was redirected from template selection or AI generation
  const isFromTemplate = location.pathname.includes('/edit/') || 
                        location.state?.fromTemplate || 
                        new URLSearchParams(location.search).get('redirect')?.includes('/edit/');
  
  const isFromAI = location.state?.fromAI || 
                  new URLSearchParams(location.search).get('from') === 'ai';
  
  const redirectMessage = location.state?.message;

  // Helper function to handle post-authentication redirects
  const handlePostAuthRedirect = async () => {
    if (isFromTemplate && !isFromAI) {
      // Coming from template - check subscription status
      try {
        const { data: statusData } = await getSubscriptionStatus();
        if (statusData.active) {
          // Has subscription - go to template editing
          const redirectPath = new URLSearchParams(location.search).get('redirect') || '/';
          navigate(redirectPath, { 
            state: location.state?.templateData ? { ...location.state } : undefined 
          });
        } else {
          // No subscription - go to subscription page
          navigate('/subscribe', {
            state: {
              fromTemplate: true,
              message: 'Choose a plan to access our premium templates and start creating!',
              templateData: location.state?.templateData
            }
          });
        }
      } catch (err) {
        console.error('Failed to check subscription status:', err);
        // If subscription check fails, still redirect to subscription page to be safe
        navigate('/subscribe', {
          state: {
            fromTemplate: true,
            message: 'Choose a plan to access our premium templates and start creating!',
            templateData: location.state?.templateData
          }
        });
      }
    } else {
      // Not from template, or from AI - normal redirect
      const redirectPath = new URLSearchParams(location.search).get('redirect') || '/';
      navigate(redirectPath, { 
        state: location.state?.templateData ? { ...location.state } : undefined 
      });
    }
  };

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      handlePostAuthRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, location, isFromTemplate, isFromAI]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuggestSignUp(false);
    setSuggestSignIn(false);
    
    const result = await authenticate({ email, password }, 'login');
    if (result.success) {
      // Don't navigate here - let the useEffect handle it after user state updates
      // The redirect will happen automatically when user state changes
    } else {
      setError(result.error);
      // Check if we should suggest alternative action
      setSuggestSignUp(shouldSuggestSignUp(result.originalError));
      setSuggestSignIn(shouldSuggestSignIn(result.originalError));
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);
    setSuggestSignUp(false);
    setSuggestSignIn(false);
    
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await getIdToken(result.user);
      
      const resp = await apiGoogleSignIn(idToken);
      localStorage.setItem('jwt_token', resp.data.idToken);
      
      // Don't navigate here - let the useEffect handle it after user state updates
      // Force a page refresh to update auth state
      window.location.reload();
      
    } catch (err) {
      console.error('Google sign-in error:', err);
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
    } finally {
      setGoogleLoading(false);
    }
  };

  const getContextualMessage = () => {
    if (redirectMessage) return redirectMessage;
    if (isFromAI) return 'Sign in to create AI-powered greeting cards with advanced customization';
    if (isFromTemplate) return 'Create an account or sign in to start creating amazing greeting cards';
    return 'Sign in to your account to continue creating amazing greeting cards';
  };

  const getContextualTitle = () => {
    if (isFromAI) return 'Sign in for AI Card Creation';
    if (isFromTemplate) return 'Sign in to continue';
    return 'Welcome back';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <SEO
        title="Login"
        description="Sign in to your DreamableAI account to create beautiful AI greeting cards."
        path="/login"
        noindex={true}
      />
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-lg shadow-2xl p-8">
          {/* Header */}
          <div className="text-center">
            <Link to="/" className="text-3xl font-bold text-purple-600 hover:text-purple-700 transition">
              🎨 DreamableAI
            </Link>
            <h2 className="mt-6 text-2xl font-bold text-gray-900">
              {getContextualTitle()}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {getContextualMessage()}
            </p>
          </div>

          {/* AI Generation Notice */}
          {isFromAI && (
            <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-purple-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-purple-700">
                    <strong>Unlock AI-Powered Creation!</strong><br />
                    Sign in to access our advanced AI tools that can generate personalized greeting cards in seconds.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Template Access Notice */}
          {isFromTemplate && !isFromAI && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Create an account to access templates!</strong><br />
                    Sign up for free to start customizing greeting cards with our beautiful templates.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message with inline suggestions */}
          {error && (
            <div className="mt-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    {suggestSignUp ? (
                      <p className="text-sm text-red-600">
                        Invalid email or password. Don't have an account?{' '}
                        <Link to="/register" className="font-semibold text-purple-600 hover:text-purple-500 underline">
                          Sign up here
                        </Link>
                      </p>
                    ) : (
                      <p className="text-sm text-red-600">{error}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 appearance-none relative block w-full px-3 py-3 border border-gray-300 rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your email"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 appearance-none relative block w-full px-3 py-3 border border-gray-300 rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Link
                to="/forgot-password"
                className="text-sm text-purple-600 hover:text-purple-500 font-medium"
              >
                Forgot your password?
              </Link>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
              >
                {loading ? (
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : null}
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-2 text-gray-500">Or continue with</span>
              </div>
            </div>

            {/* Google Sign-in Button */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading || googleLoading}
              className="w-full flex items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {googleLoading ? 'Signing in...' : 'Sign in with Google'}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="font-medium text-purple-600 hover:text-purple-500 transition"
              >
                Create one now
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 